import React, { useState, useEffect } from 'react';
import { adminAPI, photoAPI } from '../../lib/api';
import { Plus, Trash2, Pencil, X, Image, Eye, EyeOff, ArrowUp, ArrowDown, ExternalLink, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export default function AdminBannersTab() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', subtitle: '', image_url: '', link: '', position: 'hero' });
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI.getBanners().then(r => setBanners(r.data.banners)).catch(() => toast.error('Erreur chargement')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ title: '', subtitle: '', image_url: '', link: '', position: 'hero' });
    setEditing(null);
    setShowForm(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await photoAPI.upload(file);
      const photoId = res.data.photo_id;
      const url = photoAPI.getUrl(photoId);
      setForm(p => ({ ...p, image_url: url }));
      toast.success('Image uploadée');
    } catch {
      toast.error("Erreur d'upload");
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.image_url) { toast.error('Titre et image requis'); return; }
    try {
      if (editing) {
        await adminAPI.updateBanner(editing, form);
        toast.success('Bannière mise à jour');
      } else {
        await adminAPI.createBanner(form);
        toast.success('Bannière créée');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette bannière ?')) return;
    try { await adminAPI.deleteBanner(id); toast.success('Supprimée'); load(); } catch { toast.error('Erreur'); }
  };

  const handleToggle = async (banner) => {
    try {
      await adminAPI.updateBanner(banner.id, { active: !banner.active });
      toast.success(banner.active ? 'Masquée' : 'Affichée');
      load();
    } catch { toast.error('Erreur'); }
  };

  const handleReorder = async (banner, direction) => {
    const newOrder = direction === 'up' ? Math.max(0, banner.order - 1) : banner.order + 1;
    try {
      await adminAPI.updateBanner(banner.id, { order: newOrder });
      load();
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (banner) => {
    setForm({ title: banner.title, subtitle: banner.subtitle || '', image_url: banner.image_url, link: banner.link || '', position: banner.position || 'hero' });
    setEditing(banner.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#033871]">Bannières & Images promo</h2>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0C63BF] hover:bg-[#033871] gap-2 h-9 text-sm" data-testid="add-banner-btn">
          <Plus className="w-4 h-4" /> Nouvelle bannière
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5 space-y-4" data-testid="banner-form">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#033871] text-sm">{editing ? 'Modifier la bannière' : 'Nouvelle bannière'}</h3>
            <button onClick={resetForm} className="text-[#5A8BA8] hover:text-[#033871]"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Titre</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Titre de la bannière" className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="banner-title-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Sous-titre</label>
              <input value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))}
                placeholder="Sous-titre (optionnel)" className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="banner-subtitle-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Lien (URL)</label>
              <input value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))}
                placeholder="/catalogue ou https://..." className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="banner-link-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Position</label>
              <select value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))}
                className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="banner-position-select">
                <option value="hero">Hero (haut de page)</option>
                <option value="promo">Promotionnel (milieu de page)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Image</label>
            <div className="flex gap-3 items-start">
              <div className="flex-1">
                <input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
                  placeholder="URL de l'image ou uploader" className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="banner-imageurl-input" />
              </div>
              <label className={`h-10 px-4 rounded-xl border border-[#D6E8F0] flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F0F7FA] transition-colors ${uploading ? 'opacity-50' : ''}`}>
                <Upload className="w-4 h-4 text-[#5A8BA8]" />
                <span className="text-[#5A8BA8]">{uploading ? 'Upload...' : 'Upload'}</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} data-testid="banner-upload-input" />
              </label>
            </div>
            {form.image_url && (
              <div className="mt-3 rounded-xl overflow-hidden border border-[#E8F4FD] h-32">
                <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={resetForm} variant="outline" className="h-9 text-sm">Annuler</Button>
            <Button onClick={handleSubmit} className="bg-[#0C63BF] hover:bg-[#033871] h-9 text-sm" data-testid="banner-submit-btn">
              {editing ? 'Mettre à jour' : 'Créer la bannière'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-3 border-[#0C63BF] border-t-transparent rounded-full animate-spin" /></div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-10 text-center">
          <Image className="w-10 h-10 text-[#B8D4E3] mx-auto mb-3" />
          <p className="text-sm text-[#5A8BA8]">Aucune bannière. Cliquez sur "Nouvelle bannière" pour en créer une.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((banner, idx) => (
            <div key={banner.id} className={`bg-white rounded-2xl border overflow-hidden ${banner.active ? 'border-[#E8F4FD]' : 'border-red-100 opacity-60'}`} data-testid={`banner-${banner.id}`}>
              <div className="flex">
                <div className="w-32 h-24 shrink-0 bg-[#F0F7FA]">
                  <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#033871] text-sm truncate">{banner.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${banner.position === 'hero' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                      {banner.position === 'hero' ? 'Hero' : 'Promo'}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${banner.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                      {banner.active ? 'Active' : 'Masquée'}
                    </span>
                  </div>
                  {banner.subtitle && <p className="text-xs text-[#5A8BA8] mt-0.5 truncate">{banner.subtitle}</p>}
                  {banner.link && <p className="text-[10px] text-[#0C63BF] mt-0.5 flex items-center gap-1 truncate"><ExternalLink className="w-3 h-3" /> {banner.link}</p>}
                </div>
                <div className="flex items-center gap-1 p-2 shrink-0">
                  <button onClick={() => handleReorder(banner, 'up')} disabled={idx === 0} className="p-1.5 rounded-lg text-[#5A8BA8] hover:bg-[#F0F7FA] disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => handleReorder(banner, 'down')} disabled={idx === banners.length - 1} className="p-1.5 rounded-lg text-[#5A8BA8] hover:bg-[#F0F7FA] disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => handleToggle(banner)} className={`p-1.5 rounded-lg transition-colors ${banner.active ? 'text-emerald-600 hover:bg-emerald-50' : 'text-red-500 hover:bg-red-50'}`} data-testid={`banner-toggle-${banner.id}`}>
                    {banner.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => startEdit(banner)} className="p-1.5 rounded-lg text-[#5A8BA8] hover:bg-[#F0F7FA] hover:text-[#0C63BF]" data-testid={`banner-edit-${banner.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(banner.id)} className="p-1.5 rounded-lg text-[#5A8BA8] hover:bg-red-50 hover:text-red-500" data-testid={`banner-delete-${banner.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
